import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { isQrPromoReservedSlug } from './reservedUrlSlug';

export function cn(...inputs) {
	return twMerge(clsx(inputs));
}

/**
 * Capitalizes the first letter of every word in a string
 * Only applies if text is all lowercase or mixed case
 * Leaves all caps or already properly formatted text unchanged
 * @param {string} str - The input string
 * @returns {string} - The string with first letter of each word capitalized (if needed)
 */
export const capitalizeWords = (str) => {
  if (!str || typeof str !== 'string') return str;
  
  // Check if string is all uppercase (leave as is)
  if (str === str.toUpperCase() && str.length > 1) {
    return str;
  }
  
  // Check if string already has proper capitalization (has uppercase letters in middle of words)
  // This is a simple heuristic - if there are uppercase letters that aren't at word starts, leave it
  const words = str.split(' ');
  let hasMixedCase = false;
  for (const word of words) {
    if (word.length > 1) {
      // Check if there are uppercase letters after the first character
      const restOfWord = word.slice(1);
      if (/[A-Z]/.test(restOfWord)) {
        hasMixedCase = true;
        break;
      }
    }
  }
  
  // If it already has mixed case formatting, leave it as is
  if (hasMixedCase) {
    return str;
  }
  
  // Otherwise, capitalize first letter of each word
  return words
    .map(word => {
      if (word.length === 0) return word;
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
};

// Utility function to check if a URL slug is available
export const checkSlugAvailability = async (slug, currentUserId = null) => {
  if (!slug) return true;
  if (isQrPromoReservedSlug(slug)) return false;

  try {
    const { collection, query, where, getDocs } = await import('firebase/firestore');
    const { db } = await import('./firebase');
    
    const profilesRef = collection(db, 'profiles');
    const q = query(profilesRef, where('url_slug', '==', slug.toLowerCase()));
    const querySnapshot = await getDocs(q);
    
    // If no other profiles use this slug, it's available
    if (querySnapshot.empty) {
      return true;
    }
    
    // If this slug is used by the current user (updating their own profile), it's available
    if (currentUserId && querySnapshot.docs[0].id === currentUserId) {
      return true;
    }
    
    // Slug is taken by another user
    return false;
  } catch (error) {
    console.error('Error checking slug availability:', error);
    
    // If it's a permissions error, we'll assume the slug is available
    // This prevents the app from breaking due to security rules
    if (error.code === 'permission-denied' || error.message.includes('Missing or insufficient permissions')) {
      console.warn('Firestore permissions issue - assuming slug is available');
      return true;
    }
    
    // For other errors, we'll be conservative and assume it's not available
    return false;
  }
};

// Utility function to generate a URL slug from a name
// NEW: PascalCase without dashes, only alphanumeric characters
export const generateUrlSlug = (name) => {
  if (!name) return '';

  const parts = String(name)
    .replace(/[^a-zA-Z0-9]+/g, ' ') // normalize non-alphanumerics to spaces
    .trim()
    .split(/\s+/);

  const pascal = parts
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join('');

  return pascal;
};